import {useState, useCallback, type MouseEvent, type PropsWithChildren, type ReactNode} from 'react'
import {ChevronDownIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import styles from './ForYouItem.module.css'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {useRegisterAsVisible} from './ForYouSubSection'

interface ForYouItemBaseProps {
  /** Leading visual displayed before the title - prefer using ForYouLeadingIcon */
  leadingVisual: ReactNode
  /** Name of the item for analytics purposes */
  itemName: string
  /** Whether the item starts expanded (default: false) */
  defaultOpen?: boolean
  /** Optional slot displayed on the trailing side of the header */
  actionSlot?: ReactNode
  /** Optional description rendered below the title row — a string is rendered with the description style, a ReactNode is rendered as-is */
  description?: string | ReactNode
  /** Optional padding for the item */
  padding?: 'default' | 'none'
}
interface ForYouItemStringTitleProps extends ForYouItemBaseProps {
  /** Heading displayed in the header, rendered with the title style */
  title: string
  /** Accessible label for the toggle button. Optional for string titles — defaults to the title */
  ariaLabel?: string
}

interface ForYouItemNodeTitleProps extends ForYouItemBaseProps {
  /** Heading displayed in the header, rendered as-is */
  title: ReactNode
  /** Accessible label for the toggle button. Required when the title is a ReactNode */
  ariaLabel: string
}

export type ForYouItemProps = ForYouItemStringTitleProps | ForYouItemNodeTitleProps

export function ForYouItem({
  leadingVisual,
  title,
  ariaLabel,
  itemName,
  actionSlot,
  description,
  children,
  defaultOpen = false,
  padding = 'default',
}: PropsWithChildren<ForYouItemProps>) {
  const isExpandable = !!children
  const [isExpanded, setIsExpanded] = useState(defaultOpen)
  if (isExpanded && !isExpandable) setIsExpanded(false)

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  // Register as visible in the parent ForYouSubSection so the section heading is shown
  // Consumers of ForYouItem should early return null if they have no content to show
  // once we've got this far we know the item is visible and should be registered
  useRegisterAsVisible()

  const toggle = useCallback(() => {
    const next = !isExpanded
    sendPullRequestAnalyticsEvent('overview.for_you_item_toggle', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: itemName,
      is_open: next,
    })
    setIsExpanded(next)
  }, [isExpanded, sendPullRequestAnalyticsEvent, itemName])

  const handleHeaderClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target instanceof Element && e.target.closest('a, button')) return
      toggle()
    },
    [toggle],
  )

  const titleContent = typeof title === 'string' ? <span className={styles.title}>{title}</span> : title
  const descriptionContent =
    description != null ? (
      typeof description === 'string' ? (
        <span className={styles.description}>{description}</span>
      ) : (
        description
      )
    ) : null
  const labelText = ariaLabel ?? (typeof title === 'string' ? title : '')
  const toggleAriaLabel = `${isExpanded ? 'Collapse' : 'Expand'} ${labelText}`.trim()

  return (
    <li className={styles.container}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- keyboard a11y is handled by the nested toggle button */}
      <div
        className={clsx(styles.headerRow, isExpandable && styles.expandable, isExpanded && styles.expanded)}
        onClick={isExpandable ? handleHeaderClick : undefined}
      >
        <span className={clsx(styles.leadingVisual)}>{leadingVisual}</span>
        <span className={styles.headerText}>
          <span className={styles.titleRow}>{titleContent}</span>
          {descriptionContent ? <span className={styles.descriptionRow}>{descriptionContent}</span> : null}
        </span>
        {actionSlot != null ? <span className={styles.trailing}>{actionSlot}</span> : null}
        {isExpandable && (
          <IconButton
            icon={ChevronDownIcon}
            aria-label={toggleAriaLabel}
            aria-expanded={isExpanded}
            onClick={toggle}
            size="small"
            variant="invisible"
            className={clsx(styles.chevronButton, !isExpanded && styles.chevronCollapsed)}
          />
        )}
      </div>
      {isExpanded && <div className={clsx(styles.body, padding === 'none' && styles.noPadding)}>{children}</div>}
    </li>
  )
}
